import { parseDateValue } from '../utils/formatUtils';
// src/stores/topicsIntensityStore.js
import { makeAutoObservable } from 'mobx';
import axios from 'axios';
import { getAPIUrl } from '../api/apiUtils';
import { 
  isNumericTopicsIntensityMarker, 
  topicsIntensityMarkersDictionary, 
  isTopicsIntensityMarkerHidden 
} from './topicsIntensityMarkersDictionary';
import { groupIdToTitle } from '../utils/groupTitles';

const API_BASE_URL = getAPIUrl();

// Простая функция для сравнения через сериализацию
function paramsEqual(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}

class TopicsIntensityStore {
  // Табличные данные
  tableData = [];
  totalRows = 0;
  pageCount = 1;
  loadingTable = false;
  errorTable = null;

  // Данные графика
  chartData = [];
  loadingChart = false;
  errorChart = null;

  // Параметры
  selectedMarkers = ['dict', 'intensity'];
  aggregation = '1'; // '1' = day, '7' = week, '30' = month
  
  // Модальное окно экспорта
  showExportModal = false;

  // Кэш последних параметров
  lastTableParams = null;
  lastChartParams = null;

  constructor() {
    makeAutoObservable(this);
  }

  setSelectedMarkers(markers) {
    this.selectedMarkers = markers;
  }

  setAggregation(value) {
    this.aggregation = value;
  }
  
  setShowExportModal(value) {
    this.showExportModal = value;
  }

  // Формируем параметры для таблицы (для сравнения)
  _buildTableParams({ pageIndex, pageSize, sorting, globalFilters }) {
    return {
      selectedMarkers: [...this.selectedMarkers].sort(),
      pageIndex,
      pageSize,
      sorting: sorting.map(s => ({ id: s.id, desc: s.desc })),
      globalFilters: {
        selectedGroups: [...(globalFilters.selectedGroups || [])].sort(),
        startDate: globalFilters.startDate,
        endDate: globalFilters.endDate,
      },
    };
  }

  // Формируем параметры для графика
  _buildChartParams(globalFilters) {
    return {
      selectedMarkers: [...this.selectedMarkers].sort(),
      aggregation: this.aggregation,
      globalFilters: {
        selectedGroups: [...(globalFilters.selectedGroups || [])].sort(),
        startDate: globalFilters.startDate,
        endDate: globalFilters.endDate,
      },
    };
  }

  async fetchTableData({ pageIndex, pageSize, sorting, globalFilters }) {
    const newParams = this._buildTableParams({ pageIndex, pageSize, sorting, globalFilters });

    // Проверяем, изменились ли параметры
    if (this.lastTableParams && paramsEqual(this.lastTableParams, newParams)) {
      console.log('Таблица: параметры не изменились — запрос не отправляется');
      return;
    }

    if (this.selectedMarkers.length === 0) {
      this.tableData = [];
      this.totalRows = 0;
      this.pageCount = 1;
      this.lastTableParams = newParams;
      return;
    }

    this.loadingTable = true;
    this.errorTable = null;

    try {
      const sort = sorting[0] || { id: 'date', desc: false };
      const page = pageIndex + 1;

      const columns = [
        ...this.selectedMarkers,
        ...Object.keys(topicsIntensityMarkersDictionary).filter(
          key => isTopicsIntensityMarkerHidden(key) && !this.selectedMarkers.includes(key)
        )
      ];

      const { selectedGroups, startDate, endDate } = globalFilters;

      const response = await axios.get(`${API_BASE_URL}/detection/topics_intensity_analysis`, {
        params: {
          page,
          limit: pageSize,
          sort_by: sort.id,
          sort_order: sort.desc ? 'desc' : 'asc',
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          columns: columns.length > 0 ? columns.join(',') : undefined,
          group_ids: selectedGroups.length > 0 ? selectedGroups.join(',') : undefined,
        },
      });
      console.log('RESPONSE Table Data:', response.data);

      const total = Number(response.data.total) || 0;
      const size = Number(pageSize) || 10;

      this.tableData = response.data.data || [];
      this.totalRows = total;
      this.pageCount = Math.ceil(total / size) || 1;
      this.lastTableParams = newParams; // Сохраняем параметры
    } catch (error) {
      console.error('Ошибка загрузки таблицы:', error);
      this.errorTable = 'Не удалось загрузить данные таблицы';
      this.tableData = [];
      this.totalRows = 0;
      this.pageCount = 1;
    } finally {
      this.loadingTable = false;
    }
  }

  async fetchChartData(globalFilters) {
    const newParams = this._buildChartParams(globalFilters);

    if (this.lastChartParams && paramsEqual(this.lastChartParams, newParams)) {
      console.log('График: параметры не изменились — запрос не отправляется');
      return;
    }

    if (this.selectedMarkers.length === 0) {
      this.chartData = [];
      this.errorChart = null;
      this.lastChartParams = newParams;
      return;
    }

    this.loadingChart = true;
    this.errorChart = null;

    try {
      const { selectedGroups, startDate, endDate } = globalFilters;
      const chartMarkers = this.selectedMarkers.filter(m => isNumericTopicsIntensityMarker(m));

      if (chartMarkers.length === 0) {
        this.chartData = [];
        this.lastChartParams = newParams;
        return;
      }

      const period = this.aggregation === '1' ? 'day' : this.aggregation === '7' ? 'week' : 'month';

      const response = await axios.get(`${API_BASE_URL}/detection/topics_intensity_chart_data`, {
        params: {
          group_ids: selectedGroups.length > 0 ? selectedGroups.join(',') : undefined,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          markers: chartMarkers.join(','),
          period,
        },
      });
      console.log('RESPONSE Chart Data:', response.data);
      console.log("chartMarkers: ", chartMarkers);
      const transformedData = response.data.map(group => {
        const dictValue = group.data[0]?.dict || 'unknown';
        return {
          ...group,
          marker: dictValue,
          data: group.data.map(item => ({ ...item, dict: undefined })),
        };
      });

      this.chartData = transformedData || [];
      this.lastChartParams = newParams; // Сохраняем параметры
    } catch (error) {
      console.error('Ошибка загрузки графика:', error);
      this.errorChart = 'Не удалось загрузить данные для графика';
      this.chartData = [];
    } finally {
      this.loadingChart = false;
    }
  }

  async fetchTableDataByDate(date, globalFilters) {
    console.log("fetchTableDataByDate: ", date, globalFilters);
    if (!date) return [];
  
    try {
      const columns = [
        ...this.selectedMarkers,
        ...Object.keys(topicsIntensityMarkersDictionary).filter(
          key => isTopicsIntensityMarkerHidden(key) && !this.selectedMarkers.includes(key)
        ),
      ];
  
      const { selectedGroups } = globalFilters;
  
      const response = await axios.get(`${API_BASE_URL}/detection/topics_intensity_analysis`, {
        params: {
          limit: 10000000,
          sort_by: 'date',
          sort_order: 'asc',
          start_date: date,
          end_date: date,
          columns: columns.length > 0 ? columns.join(',') : undefined,
          group_ids: selectedGroups.length > 0 ? selectedGroups.join(',') : undefined,
        },
      });
  
      console.log(`RESPONSE Table Data by date (${date}):`, response.data);
      const raw = response.data?.data || [];

      // Маппер ключ -> русское название
      const labelOf = (key) => {
        const def = topicsIntensityMarkersDictionary[key];
        return def?.label || key;
      };

      // Построение ссылок ВК
      const buildPostLink = (groupId, postId) => {
        if (!groupId || !postId) return '';
        const gid = Number(groupId);
        const vkGroupId = isNaN(gid) ? '' : (gid > 0 ? -gid : gid);
        if (vkGroupId === '') return '';
        return `https://vk.com/wall${vkGroupId}_${postId}`;
      };
      

      // Переименуем столбцы на русский и добавим ссылки
      const transformed = raw.map((row) => {
        const result = {};
        Object.keys(row).forEach((key) => {
          result[labelOf(key)] = row[key];
        });

        // Преобразование даты API в yyyy-MM-dd
        if (row.date != null) {
          const d = parseDateValue(row.date);
          if (d) {
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            result['Дата'] = `${yyyy}-${mm}-${dd}`;
          }
        }

        // Добавочные колонки со ссылками (если есть необходимые поля)
        const postLink = buildPostLink(row.group_id, row.post_id);        
        result['Ссылка на пост'] = postLink || '-';        

        // Значение колонки "Группа" — человекочитаемое название
        if ('group_id' in row) {
          const title = groupIdToTitle[String(row.group_id)] || row.group_id;
          result['Группа'] = title;
        }

        // Удаляем ненужные системные колонки
        delete result['ID автора'];
        delete result['UID сессии'];
        delete result['Временная метка'];

        return result;
      });

      // возвращаем преобразованные данные
      return transformed;
    } catch (error) {
      console.error("Ошибка загрузки таблицы по дате:", error);
      return [];
    }
  }
  
  

  clearErrors() {
    this.errorTable = null;
    this.errorChart = null;
  }

  
}



export const topicsIntensityStore = new TopicsIntensityStore();